import { ready, firstInteraction, inView, elementWidth, css, log, logError, cdnUrl, loadPixel, loadJs, loadJsModule, isHome, isPath, getPageType } from './modules/utilities'
import { load as loadGpt } from './modules/google-publisher-tags'
import { load as loadPublisher } from './modules/publisher'
import { load as loadGdprConsent, getConsent, EVENTS as GDPR_EVENTS } from './modules/iabtcf'
import { load as loadCxense } from './modules/cxense'
import { load as loadPrebid, setupConfig } from './modules/prebid'
import { load as loadYieldbird } from './modules/yieldbird'

import {initIdealMargins, getIdealMargin, EVENTS as IDEAL_MARGIN_EVENTS, getHashMapVersion} from './modules/idealMargins'

import { applyBlueprint } from './modules/publisherBlueprint';
import { AdUnit } from './types/prebid'
import { PublisherConfig, Size, SlotKeyItterable } from './types/publisher'
import { slotType } from './modules/enums'

(async function () {

	if (window._mega && !window._mega.testMode) return

	const EVENTS = {
		SLOT_RENDERED: 'mega.slot.rendered',
		SLOT_INVIEW: 'mega.slot.inview',
		SLOT_OUTOFVIEW: 'mega.slot.outofview',
		SLOT_REFRESH: 'mega.slot.refresh',
		SLOT_FORCE_REFRESH: 'mega.slot.forcerefresh',
		SLOT_NOTFILLED: 'mega.slot.notfilled',
	}

	log('---- MEGA LOADED ----')

	window.googletag = window.googletag || { cmd: [] }

	//@ts-ignore
	if (!window.googletag.cmd) window.googletag.cmd = []

	window.ybConfiguration = window.ybConfiguration || {};
  	window.ybConfiguration = Object.assign({}, window.ybConfiguration, { integrationMethod: 'open_tag' });
	
	// window.pbjs = window.pbjs || {}
	// window.pbjs.que = window.pbjs.que || []

	window.Quickwrap = window.Quickwrap || {}; 
	window.Quickwrap.cmd = window.Quickwrap.cmd || [];

	window._mega = window._mega || {}

	let _prebidTimeout: number = 5000
	let _slots: { [key: string]: Slot } = {}
	let _observeMutationSlots: { [key: string]: Slot } = {}
	let _adUnits: AdUnit[] = []
	let consent: Consent | null = null

	const mutationObserverConfig = {
		childList: true,
		subtree: true
	};
	let mutationObserver: MutationObserver | null = null


	await loadPublisher()

	if (!window?._mega?.publisher) {
		throw new Error('MEGA Publisher config not found');
	}

	let params: URLSearchParams = (new URL(window.location.href)).searchParams

	if (params.has('mega_native_outstream')) window._mega.publisher.config.useCustomOutstreamRenderer = !(params.get('mega_native_outstream') != 'false' && params.get('mega_native_outstream') != 'no');
	if (params.has('mega_test_outstream')) window._mega.publisher.config.useCustomOutstreamRenderer = 'test';

	/* merge publisher slots with publisher blueprint */
	const publisherConfig: PublisherConfig = await applyBlueprint(window?._mega?.publisher)
	window._mega.publisher = publisherConfig;

	if (params.has('mega_debug') && publisherConfig?.debug == false) publisherConfig.debug = (params.get('mega_debug') === "" ? true : (params.get('mega_debug') ?? false));
	if (params.has('mega_notest')) publisherConfig.config.enableTestTargeting = false;

	log('---- MEGA PUBLISHER LOADED ----');

	// initialize publisher hashmap
	initIdealMargins()

	/* check if publisher has MEGA enabled */
	let enabled = publisherConfig.enabled ?? true;
	if (typeof enabled != 'boolean') {
		enabled = isPath(enabled);
	}

	if (!enabled) {
		log('-- MEGA DISABLED');

		const loadLegacyScriptWhenDisabled = publisherConfig.loadLegacyScriptWhenDisabled ?? true;
		if (loadLegacyScriptWhenDisabled) {
			log('-- MEGA SWITCH TO LEGACY SCRIPT');

			// Remove MEGA config from window
			window._mega = {};

			// Load legacy script
			loadJs('https://cdn.aa.onstuimig.nl/base/adf-tm-base-min.js');
		}

		return;
	}

	const targetingUrl = window.location.href.replace(/https?:\/\//, "").replace(/www./gi, "").replace(/[?#].*$/i, "").replace(/[^a-z_0-9]+$/gi, "").replace(/[^a-z_0-9]/gi, "_");
	log('URL targeting: url=', targetingUrl);

	/* Check if we're already in testmode, if not check if we need to load a test-script */
	const testMode = window._mega.testMode ?? false;
	if (!testMode) {
		/* check if publisher uses a test version of the MEGA script */
		let useTestScript = publisherConfig.useTestScript ?? false;
		if (typeof useTestScript != 'boolean') {
			useTestScript = isPath(useTestScript);
		}

		if (useTestScript) {
			/* check if publisher has a custom test script */
			let testScript = publisherConfig.testScript ?? '/mega/mega-test.js';

			// Add CDN url to relative script paths
			if (!testScript.includes('://')) {
				testScript = cdnUrl() + testScript;
			}

			log('-- MEGA SWITCH TO TEST SCRIPT: ' + testScript);

			// Let test script start fresh
			window._mega = {
				testMode: true
			};

			// Load test script
			loadJs(testScript);

			// Stop current script execution
			return;
		}
	}

	if (typeof window.am_closeslot !== "function") {
		window.am_closeslot = function (elementId) { //functie om lege adslots dicht te klappen
			const adElement = document?.getElementById(elementId);
			if (adElement) {
				const slotElement = <HTMLElement | null>adElement.closest('.mg-slot');
				if (slotElement) {
					slotElement.style.display = "none"
				}
			}
		}
	}

	// add change/revoke privacy settings button
	if (publisherConfig?.config?.gdprSettings && !publisherConfig?.customConsent) {
		log('-- ADD EVENT LISTENER FOR CHANGE PRIVACY SETTINGS BUTTON')
		window.addEventListener(GDPR_EVENTS.CONSENT_READY, () => {
			ready(() => {
				log('-- ADD CHANGE PRIVACY SETTINGS BUTTON')
				let gdprSettingsTemplate = publisherConfig?.config?.gdprSettings?.template ?? `<div class="mg-settings"><a onclick="window.dispatchEvent(new CustomEvent('${GDPR_EVENTS.CONSENT_SETTINGS}'));event.preventDefault()" href="#">${publisherConfig?.config?.gdprSettings?.label ?? 'Privacy instellingen aanpassen'}</a></div>`
				const gdprSettingsTarget: HTMLElement | null = document.querySelector(publisherConfig?.config?.gdprSettings?.inject?.target ?? 'body')
				if (gdprSettingsTarget) gdprSettingsTarget.insertAdjacentHTML(publisherConfig?.config?.gdprSettings?.inject?.position ?? 'beforeend', gdprSettingsTemplate)
			})
		})
	}

	if (publisherConfig?.config?.cxense?.enabled) {
		window.addEventListener(GDPR_EVENTS.CONSENT_READY, () => {
			loadCxense(() => {
				if (consent?._purposes[1] == true && consent._purposes[3] == true) {
					if (publisherConfig?.config?.cxense?.siteId) {
						window.cX.callQueue.push([
							'invoke',
							function () {
								if (publisherConfig?.config?.cxense?.siteId) {
									window.cX.setSiteId(publisherConfig.config.cxense.siteId);
								}
							}
						])
					}
				}

				//let persistedQueryId: string = "0ca86fb91fe959596a17e7e8f7deaf0f85191f13"
				let persistedQueryId: string = "e988dfac13839d3e12773be6d388b90241285fae"

				// new cX context piano code
				log('-- PIANO getSegments starting...')
				window.cX.callQueue.push([
					'invoke',
					function () {
						window.cX.getSegments(persistedQueryId, function (segments: string[]) {
							let ids: any[] = []
							let onlyContextualShortIds: any[] = []
							let allSegments: any[] = []
							log('-- PIANO getSegments invoked with segments: ', segments)

							if (segments.length) {
								segments.forEach(function (cxTypedSegment:any, index:number) {
									if (cxTypedSegment.type === "contextual") {
										onlyContextualShortIds.push(cxTypedSegment.shortId)
									} else {
										ids.push(cxTypedSegment.id)
									}

									allSegments.push(cxTypedSegment.id)
								})
							}

							log('-- PIANO getSegments invoked with allSegments: ', allSegments)
							log('-- PIANO getSegments invoked with ids: ', ids)
							log('-- PIANO getSegments invoked with onlyContextualShortIds: ', onlyContextualShortIds)

							googletag.cmd.push(function() {
								if (ids.length) googletag.pubads().setTargeting("CxSegments", ids)
								if (onlyContextualShortIds.length) googletag.pubads().setTargeting("CxContext", onlyContextualShortIds)
								if (segments.length) googletag.pubads().setTargeting("AdtargetSegments", allSegments)
							})
						})
					}
				])
				
				
				/*
				window.cX.callQueue.push([
					'getUserSegmentIds', 
					{
						persistedQueryId: persistedQueryId,
						callback: function(segments: string[]) {
							if(typeof localStorage === 'object' && typeof localStorage.getItem === 'function') {
								localStorage.setItem("_cX_segmentInfo", segments.join(","))
	
								googletag.cmd.push(function() {
									var segments: string[] = [];
									if(typeof localStorage === 'object' && typeof localStorage.getItem === 'function') {
										const cX_segmentInfo = localStorage.getItem("_cX_segmentInfo");
										if(cX_segmentInfo !== null && cX_segmentInfo.length > 0){
											segments = cX_segmentInfo.split(',')
										}
									}
									if (segments.length) googletag.pubads().setTargeting("AdtargetSegments", segments)
								})
							}
						}
					}
				])
				*/


			})
		})
	}


	if (!publisherConfig?.skipConsent) {
		log('-- LOAD AND AWAIT CONSENT')
		// wait for consent
		consent = await loadGdprConsent(publisherConfig)
	}

	if (publisherConfig?.slots) {
		_slots = Object.assign(_slots, publisherConfig.slots)
	}
	if (publisherConfig?.adUnits?.length) {
		_adUnits = _adUnits.concat(publisherConfig.adUnits)
	}




	if (typeof publisherConfig?.onBeforeStart != 'function') {
		publisherConfig.onBeforeStart = () => { }
	}

	if (publisherConfig?.skipFirstInteraction) {
		ready(() => {
			log('-- DOCUMENT READY')
			initMega()
		})
	} else {
		firstInteraction(async () => {
			log('-- FIRST INTERACTION DONE')
			initMega()
		})
	}

	async function initMega(): Promise<any> {
		loadGpt(() => { })
		loadYieldbird(() => {
			log('-- Yieldbird loaded')
		})
		// loadPrebid(cdnUrl() + '/mega/prebidjs/prebid.js', async () => {
		// 	log('-- Prebid config and modules')
		// 	log('-- Prebid loaded?', typeof window.pbjs.processQueue === 'function')

		// 	const adapters = [
		// 		import(/* @vite-ignore */ cdnUrl() + '/mega/prebidjs/adapter-smartx.js'),
		// 		import(/* @vite-ignore */ cdnUrl() + '/mega/prebidjs/adapter-appnexus.js'),
		// 		import(/* @vite-ignore */ cdnUrl() + '/mega/prebidjs/adapter-ix.js'),
		// 		import(/* @vite-ignore */ cdnUrl() + '/mega/prebidjs/adapter-improvedigital.js'),
		// 		import(/* @vite-ignore */ cdnUrl() + '/mega/prebidjs/adapter-pubmatic.js')
		// 	];

		// 	// Wait untill all Prebid adapters are imported
		// 	await Promise.all(adapters);

		// 	window.pbjs.que.push(function () {
		// 		setupConfig()
		// 	})

		// 	window.pbjs.processQueue()
		// })

		function prepareSlot(slot: Slot, slotKey: string = '', options: { target?: Document | HTMLElement } = {}) {
			const searchTarget: Document | HTMLElement = options?.target || document
			const slotTarget: Element | null = typeof slot?.inject?.target === 'string' ? searchTarget.querySelector(slot?.inject?.target) : slot?.inject?.target
			log(`-- Slot ${slot.id} preparing to go here:`, slotTarget)

			if (!slotTarget) {
				log(`-- Slot ${slot.id} is not found; added to the MutationObserver`)
				if (!_observeMutationSlots[slotKey]) _observeMutationSlots[slotKey] = slot
				return false; // skip if target doesn't exist
			}

			if (!(slotTarget instanceof Element)) {
				log(`-- Slot ${slot.id} is has an invalid target`)
				return false; // skip if target isn't a valid element
			}

			if (_observeMutationSlots[slotKey]) delete _observeMutationSlots[slotKey]

			if (!slot.injectOriginal) {
				//log('inject original',slot.injectOriginal)
				slot.injectOriginal = Object.assign({}, slot.inject)
			}

			slot.inject.target = slotTarget;

			return true;
		}

		function addSlot(slot: Slot) {
			if (!slot?.inject?.target || !(slot?.inject?.target instanceof Element)) {
				log(`-- Slot ${slot.id} is has non Element target`)
				return // skip if target isn't a valid element
			}

			const slotTarget: Element = slot?.inject?.target
			log(`-- Slot ${slot.id} should go here:`, slotTarget)


			// create slot element
			const slotElement = slot._el ?? document.createElement('div')
			slotElement.id = slot.id
			slotElement.classList.add('mg-slot')
			slotElement.setAttribute('aria-hidden', 'true')

			// inject at position relative from target element
			slotTarget.insertAdjacentElement(slot.inject.position, slotElement)

			const poweredElement: HTMLAnchorElement = document.createElement('a')
			poweredElement.classList.add('mg-powered')
			poweredElement.setAttribute('href', 'https://adalliance.nl/')
			poweredElement.setAttribute('target', '_blank')
			slotElement.insertAdjacentElement('beforeend', poweredElement)

			slot._el = slotElement

			// measure position on page and remove element that are above or in the viewport
			if (!['static'].includes(slot?.type)) {
				const visible: any = inView(slotElement)

				if (['lazy'].includes(slot?.type)) {
					// load slot by intersectionobserver if visible (don't do anything here, but set opacity to invisible)
					slot._el.style.opacity = '0.001'
				} else {
					// this is the default: slot is not loaded if slot position is above view
					if (visible != 'below') {
						// slot is in view or above view, so remove slot
						slotElement.remove()
						slot._removed = true
						return;
					}
				}
			} else {
				// static types are always handled as inView and loaded
				slot._el.style.opacity = '0.001'
				slot._el._inView = true
			}

			// content slotElement to slot object
			slotElement._slot = slot

			// default options
			slot._renderTries = 0
			slot._retryTimeout = false
			slot._refresh = false // refresh when inView boolean
			slot._isRefreshing = false // is currently Refreshing boolean
			slot._displayed = false // is the .display function called?

			if (!slot.onSlotInjected) slot.onSlotInjected = (slot: Slot) => { } // default
			if (!slot.onSlotRendered) slot.onSlotRendered = (slot: Slot) => { } // default

			slot.onSlotInjected.call(window, slot, { css: css.bind(slot._el) })

			let adUnitsPerSlot: Array<AdUnit> = _adUnits.filter((adUnit): boolean => {
				return adUnit.code === slot.path
			})

			/**
			 * outofview, do things
			 */
			slot._el.addEventListener(EVENTS.SLOT_OUTOFVIEW, function (this: HTMLElement): void {
				if (!['static'].includes(slot?.type)) {
					slot._el._inView = false
				}
			})

			/**
			 * if slot is inView and not yet rendered, trigger SLOT_FORCE_REFRESH event
			 */
			slot._el.addEventListener(EVENTS.SLOT_INVIEW, function (this: HTMLElement): void {
				//if (observer) observer.unobserve(this)
				log('slot is inview; _slotIsRendered', slot._slotIsRendered)

				slot._el._inView = true

				if (!slot._slotIsRendered || slot._refresh) this.dispatchEvent(new CustomEvent(EVENTS.SLOT_FORCE_REFRESH)) // refresh slot when not rendered
			})

			/**
			 * if slot is inView refresh the slot, else let slot._refresh=true do the work on the SLOT_INVIEW event
			 */
			slot._el.addEventListener(EVENTS.SLOT_REFRESH, function (this: HTMLElement): void {
				//if (observer) observer.unobserve(this)
				log(`slot refresh inview trigger fired for ${slot.id}`)

				slot._refresh = true

				if (!slot._el._inView) return //not in inview, so wait for the next inView event to check if the slot may be refreshed

				log('slot refresh inview trigger -> direct refresh because slot is in view')
				// now the slot is inView, so try to refresh
				this.dispatchEvent(new CustomEvent(EVENTS.SLOT_FORCE_REFRESH)) // refresh slot when not rendered
			})

			/**
			 * init slot by calling googletag.pubads().refresh([slot._slot]) and request a bid per slot
			 */
			slot._el.addEventListener(EVENTS.SLOT_FORCE_REFRESH, function (): void {

				//log(`======CONSENT`, consent)

				if (slot._isRefreshing) {
					log(`slot is already refreshing: ${slot.id}`)
					return
				}

				log(`load or refresh ${slot.id}`)

				slot._refresh = true
				slot._isRefreshing = true


				window.Quickwrap.cmd.push(function() {
				//window.pbjs.que.push(function () {
					//window.pbjs.processQueue()

					if (!slot._displayed) {
						googletag.cmd.push(function () {
							log('display', slot.id)
							slot._displayed = true
							googletag.display(slot.id)
						})
					}

					if (slot._slotIsRendered) {
						//window.pbjs.removeAdUnit(slot.path)
					}

					if (!slot._isRequesting && typeof slot._slot !== 'undefined') {
						// give the .refresh() time to handle the request and  
						// set a request timeout for 3 seconds in which the .refresh() will not be called
						if (slot._isRequestingTimeout) clearTimeout(slot._isRequestingTimeout)
						slot._isRequestingTimeout = setTimeout(()=> {
							slot._isRequesting = false
						},3000);
					
						slot._isRequesting = true;
					
						log(`refresh bid slot ${slot.id}`)

						window.Quickwrap.refresh([slot._slot])
						
						slot._refresh = false
						slot._isRefreshing = false
					
					}

				})
			})

			/**
			 * call onSlotRendered() when SLOT_RENDERED event is triggered
			 */
			slot._el.addEventListener(EVENTS.SLOT_RENDERED, function (): void {
				slot.onSlotRendered.call(window, slot, { css: css.bind(slot._el) })

				const poweredElement: HTMLAnchorElement = document.createElement('a')
				poweredElement.classList.add('mg-powered')
				poweredElement.setAttribute('href', 'https://adalliance.nl/')
				poweredElement.setAttribute('target', '_blank')
				slot._el.insertAdjacentElement('beforeend', poweredElement)

				slot._el.style.opacity = ''

				if (slot._retryTimeout) {
					clearTimeout(slot._retryTimeout)
					slot._retryTimeout = false
				}

				// Automatically refresh slot when slot is rendered after x ms
				if (slot.refresh) {
					setTimeout(() => {
						slot._el.dispatchEvent(new CustomEvent('mega.slot.refresh'))
					}, slot.refresh)
				}
			})


			slot._el.addEventListener(EVENTS.SLOT_NOTFILLED, function (this: HTMLElement): void {
				if (!slot.retry) {
					log(`retry is false/off for ${slot.id}, no retry is fired`)
					return
				}

				slot._renderTries++

				if (slot._renderTries > 3) return

				if (slot._retryTimeout) {
					slot._renderTries-- // if the timeout is active, then tries is -1 and return

					return

					//clearTimeout(slot._retryTimeout)
					//slot._retryTimeout = false
				}

				log(`retry is on for ${slot.id}, retry timeout is starting`)

				slot._retryTimeout = setTimeout(() => {
					log(`rendering failed bid slot ${slot.id}, tried ${slot._renderTries} times`)
					if (!slot._slotIsRendered && !slot._isRefreshing) slot._el.dispatchEvent(new CustomEvent(EVENTS.SLOT_FORCE_REFRESH)) // refresh slot when not rendered
				}, _prebidTimeout + 5)
			})

			/** Define the sizes that are considered native adslot sizes */
			const nativeAdSizes: Size[] = [[510, 510], [520, 520], [530, 530], [970, 460], [970, 600]];

			/**
			 * strip all sizes wider than slot element width
			 */
			let slotContentWidth = elementWidth(slotElement)

			let filteredSizes = slot.sizes.filter((size) => {
				if (size.length != 2)
					return true;

				if (slot.native && nativeAdSizes.some((nativeSize) => JSON.stringify(nativeSize) == JSON.stringify(size)))
					return true;

				return (size[0] <= slotContentWidth);
			});

			// filter adUnit sizes by slot size, when not in takeover mode
			if (!slot.takeover) {
				for (let aui = 0; aui < adUnitsPerSlot.length; aui++) {
					const adUnit: AdUnit = adUnitsPerSlot[aui];
					if (adUnit.sizes) {
						adUnit.sizes = adUnit.sizes.filter((size) => {
							if (size.length != 2)
								return true;

							if (slot.native && nativeAdSizes.some((nativeSize) => JSON.stringify(nativeSize) == JSON.stringify(size)))
								return true

							return (size[0] <= slotContentWidth);
						})
					}
					if (adUnit.mediaTypes?.banner?.sizes) {
						adUnit.mediaTypes.banner.sizes = adUnit.mediaTypes.banner.sizes.filter((size) => {
							if (size.length != 2)
								return true;

							if (slot.native && nativeAdSizes.some((nativeSize) => JSON.stringify(nativeSize) == JSON.stringify(size)))
								return true

							return (size[0] <= slotContentWidth);
						})
					}
				}
			}

			log('-- Slot width', slot.id, slotContentWidth)
			log('-- Original slot sizes', slot.id, slot.sizes)
			log('-- Loaded slot sizes', slot.id, filteredSizes)
			log('-- Slot takeover mode', slot.id, slot.takeover ? 'on' : 'off')

			if (!filteredSizes.length) {
				if (slot._el) slot._el.remove()
				slot._removed = true

				return
			}

			// prepare slot
			googletag.cmd.push(function () {

				googletag.pubads().setTargeting("availableslots", slot.id)

				log('-- DEFINE SLOT [path:' + slot.path + '] [id:' + slot.id + ']')

				slot._slot = googletag.defineSlot(slot.path, filteredSizes, slot.id)
					?.setTargeting('Adslot', [slot.id])
					.setCollapseEmptyDiv(slot.collapseEmptyDivs ?? false)
					.addService(googletag.pubads())

				// set default slotIsRendered to false
				slot._slotIsRendered = false

				let margin:number|false = rootMargin

				let addObserverToSlot: Function = (slot:Slot) => {
					if (!slot._slot) return 

					let idealMarginIsUsed:boolean = false
					if (['static'].includes(slot._el._slot.type)) {  // only for lazy slots
						margin = 100;
					} else {
						if (slot.useIdealMargin) {
							if (Math.random() > slot.useIdealMargin) { // epsilon test
								// epsilon test exmaple 30% (0.3) of the time use ideal margin
								margin = rootMargin
							} else {
								const idealMargin = getIdealMargin(slot)
								if (idealMargin) {
									margin = idealMargin
									idealMarginIsUsed = true
								} else {
									margin = rootMargin
								}
							}
						} else {
							margin = rootMargin
						}

						slot._slot.setTargeting("lazy_loading_margin_c", margin.toString())

						if (idealMarginIsUsed) {
							const hashmapVersion = getHashMapVersion();
							slot._slot.setTargeting("lazy_loading_experiment", "hashmap"+(hashmapVersion != 0) ? "_" + hashmapVersion : "")
						} else {
							slot._slot.setTargeting("lazy_loading_experiment", "random_margin")
						}
					}
		
					log('Rootmargin for slot ' + slot.id + ' is set to:', margin)
			
					if (slot._el._observer) slot._el._observer.disconnect()

					slot._el._observer = new IntersectionObserver(
						(slotElements, observer): void => {
							slotElements.forEach((slotEntry: IntersectionObserverEntry): void => {
								slotEntry.target.dispatchEvent(new CustomEvent((slotEntry.isIntersecting) ? EVENTS.SLOT_INVIEW : EVENTS.SLOT_OUTOFVIEW))
			
								/* @todo in- or after- pilot */
								if (slotEntry.isIntersecting) {
									// after initializing, unobserve this watcher and let Google Pubads Events handle the inViews
									slot._el._observer.unobserve(slotEntry.target) // disable 100px out-of-viewport observer
			
									// megaObserverHalfInView.observe(slotEntry.target) // enable 50% in-view observer
								}
							});
						},
						{
							rootMargin: `${margin}px 0px ${margin}px 0px`
						}
					)
					slot._el._observer.observe(slot._el)
				}

				addObserverToSlot(slot)
				slot._el.addEventListener(IDEAL_MARGIN_EVENTS.SLOT_MARGIN_UPDATED, (e) => {
					log('Slot margin updated', slot.id);
					addObserverToSlot(slot)
				});
				
				// if (slot.useIdealMargin && hasIdealMargin(slot)) {
				// 	addObserverToSlot(slot)
				// } else {
				// 	// add to queue
				// 	addObserverToSlot(slot)

				// 	addToIdealMarginQueue(() => {
				// 		addObserverToSlot(slot)
				// 	})
				// }
	

				googletag.pubads().addEventListener('impressionViewable', function (event) {
					// For ads, 50% of the ad’s pixels are visible in the browser window for a continuous 1 second.
					// For larger ads (those greater than 242,000 pixels), 30% of the ad’s pixels are visible in the browser window.
					// For in-stream video ads, 50% of the ad’s pixels must be visible in the browser window for a continuous 2 seconds.

					if (event.slot === slot._slot) {
						log('impressionViewable fired', slot._el)
						slot._el._impressionViewable = true
						slot._el.dispatchEvent(new CustomEvent(EVENTS.SLOT_INVIEW))
					}
				})

				googletag.pubads().addEventListener('slotVisibilityChanged', function (event) {
					if (event.slot === slot._slot) {
						slot._el._inViewPercentage = event.inViewPercentage

						log('slotVisibilityChanged fired', slot._el, event.inViewPercentage)

						if (event.inViewPercentage >= 50) {
							//impressionViewable event is fireing the SLOT_INVIEW
							//slot._el.dispatchEvent(new CustomEvent(EVENTS.SLOT_INVIEW)) // fallback if impressionViewable event is not fired
						} else {
							slot._el._impressionViewable = false // there is no impression NOT Viewable event, so set false in here
							slot._el.dispatchEvent(new CustomEvent(EVENTS.SLOT_OUTOFVIEW))
						}
					}
				})


				// when a slot is rendered / filled, dispatch event on the element
				googletag.pubads().addEventListener('slotRenderEnded', function (event: googletag.events.SlotRenderEndedEvent): void {
					
					if (slot._isRequestingTimeout) clearTimeout(slot._isRequestingTimeout)
					slot._isRequesting = false;
			

					if (event.slot === slot._slot && !event.isEmpty) {
						log('slotRenderEnded: rendered', slot._el)
						slot._slotIsRendered = true
						slot._el.dispatchEvent(new CustomEvent(EVENTS.SLOT_RENDERED))
					}
					if (event.slot === slot._slot && event.isEmpty) {
						log('slotRenderEnded: empty', slot._el)
						slot._slotIsRendered = false
						slot._el.dispatchEvent(new CustomEvent(EVENTS.SLOT_NOTFILLED))
					}
				})

			})
		}

		
		// prepare slot observer and fire event if Slot is inview/outofview

		//const rootMarginBuckets: number[] = [0, 50, 100, 150, 200, 250, 300, 350]
		const rootMarginBuckets: number[] = [0, 100, 200, 300, 400, 500, 600]
		const rootMarginIndex: number = Math.floor(Math.random() * rootMarginBuckets.length)
		let rootMargin: number = rootMarginBuckets[rootMarginIndex]


		/* @todo in- or after- pilot */
		// NOTE: maybe below can be replaces for the impressionViewable and SlotVisibilityChanged event

		// let megaObserverHalfInView = new IntersectionObserver(
		// 	(slotElements, observer): void => {
		// 		slotElements.forEach((slotEntry: IntersectionObserverEntry): void => {
		// 			slotEntry.target.dispatchEvent(new CustomEvent((slotEntry.isIntersecting) ? EVENTS.SLOT_INVIEW : EVENTS.SLOT_OUTOFVIEW))
		// 		});
		// 	},
		// 	{
		// 		threshold: 0.5,
		// 		rootMargin: "0px"
		// 	}
		// )


		// googletag loaded, load everything
		window.googletag.cmd.push(async () => {
			log('-- GPT LOADED')

			let injectableSlots: Slot[] = [];

			// prepare slot positions
			Object.entries(_slots).forEach(entry => {
				const [slotKey, slot] = entry;

				//log(slotname, 'skip' ,slot.skip)

				if (!slot) return
				if (slot.skip) return
				if (!slot?.inject?.target || !slot?.inject?.position) return // skip if target setting doesn't exist

				//log(slotname, 'is added to the page')
				// addSlot(slot, slotKey, { target: document })

				const slotPrepared = prepareSlot(slot, slotKey, { target: document })
				if (slotPrepared) {
					injectableSlots.push(slot);
				}
			})

			// inject adslots
			injectableSlots.forEach(injectableSlot => {
				//log(slotname, 'is added to the page')
				addSlot(injectableSlot)
			});

			//if (Object.entries(_observeMutationSlots).length) {

			// Watch for changes in document content
			mutationObserver = new MutationObserver(function (mutations) {
				mutations.forEach(function (mutation) {

					//watch removed nodes
					if (mutation.removedNodes.length) {
						mutation.removedNodes.forEach((removed_node: Node) => {
							if (!(removed_node instanceof Element)) return
							let removedElements: NodeListOf<SlotHTMLElement> | null = removed_node.querySelectorAll('.mg-slot')
							if (!removedElements.length) return

							log('Removed Elements', removedElements)
							log('Loop through Removed Elements')
							for (let index: number = 0; index < removedElements.length; index++) {
								let removedElement: SlotHTMLElement = removedElements[index]

								if (removedElement && removedElement._slot && removedElement._slot._slot) {
									log('Removed Element', removedElement, removedElement?._slot)

									// slot element is removed somewhere in HTML; 1. destroy the slot and 2. put it back for watching
									googletag.destroySlots([removedElement._slot._slot])
									if (publisherConfig.slots[removedElement._slot.slotKey]
										&& removedElement._slot?.slotKey
										&& !_observeMutationSlots[removedElement._slot.slotKey]
										&& publisherConfig.slots[removedElement._slot.slotKey]
									) {
										log('Removed Element back in observer', removedElement._slot.slotKey)
										let slotKey: SlotKeyItterable = removedElement._slot.slotKey

										let slotObj: Slot = <Slot>publisherConfig.slots[slotKey]
										slotObj.inject = Object.assign({}, slotObj.injectOriginal)

										//log ('slot inject after removal',slotObj.injectOriginal)

										_observeMutationSlots[slotKey] = slotObj


									}
								}
							}

						});
					}

					if (mutation.addedNodes.length <= 0)
						return;

					// if _observeMutationSlots is empty, remove observations
					// if (!Object.entries(_observeMutationSlots).length) {
					// 	log('Disconnect MutationObserver')
					// 	mutationObserver.disconnect()
					// 	return
					// }

					const contentHolder = <HTMLElement>mutation.target;

					if (contentHolder.closest('.mg-slot'))
						return;

					if (!contentHolder.closest('body'))
						return;

					let injectableMutatedSlots: Slot[] = [];

					Object.entries(_observeMutationSlots).forEach(entry => {
						const [slotKey, slot] = entry;

						if (!_observeMutationSlots[slotKey]) return

						log(`prepareSlot by MutationObserver for ${slot.id}`)
						const slotPrepared = prepareSlot(slot, slotKey, { target: document })
						if (slotPrepared) {
							injectableMutatedSlots.push(slot);
						}

						// log(`addSlot by MutationObserver for ${slot.id}`)
						// addSlot(slot, slotKey, { target: contentHolder })

					})

					injectableMutatedSlots.forEach(injectableSlot => {
						log(`addSlot by MutationObserver for ${injectableSlot.id}`)
						addSlot(injectableSlot)
					});
				});
			});

			mutationObserver.observe(document.documentElement, mutationObserverConfig);
			//}

			document.addEventListener("visibilitychange", () => {
				if (document.hidden) {
					// inactive tab
				} else {
					log(`Document is active get all slots which have slot._refresh=true`)

					log(`All slots`, _slots)

					// tab is active...trigger refresh
					Object.entries(_slots).filter(s => s[1]._refresh).forEach(entry => {
						const [slotname, slot] = entry;

						log(`Slot loop: ${slot.id}`, slot._el, slot._el._inView, slot._el)

						if (slot._el && slot._el._inView) {
							log(`Trigger slot ${slot.id} refresh trigger on document active`)
							slot._el.dispatchEvent(new CustomEvent(EVENTS.SLOT_FORCE_REFRESH)) // refresh slot when not rendered
						}
					})
				}
			});

			log('-- ADSLOTS INJECTED')

			// enable googletag pubads
			googletag.cmd.push(function () {

				googletag.pubads().setTargeting("takeover", window.innerWidth >= 1600 ? "true" : "false")

				log('-- Set targeting by consent')

				if (consent?._purposes) {
					googletag.pubads().setTargeting("IAB_1_information_storage_access", consent?._purposes[1] ? 'true' : 'false')
					googletag.pubads().setTargeting("IAB_2_Basic_ad_selection", consent?._purposes[2] ? 'true' : 'false')
					googletag.pubads().setTargeting("IAB_3_Personalised_ad_profile", consent?._purposes[3] ? 'true' : 'false')
					googletag.pubads().setTargeting("IAB_4_Personalised_ad_selection", consent?._purposes[4] ? 'true' : 'false')
					googletag.pubads().setTargeting("IAB_5_Personalised_content_profile", consent?._purposes[5] ? 'true' : 'false')
					googletag.pubads().setTargeting("IAB_6_Personalised_content_selection", consent?._purposes[6] ? 'true' : 'false')
					googletag.pubads().setTargeting("IAB_7_Ad_measurement", consent?._purposes[7] ? 'true' : 'false')
					googletag.pubads().setTargeting("IAB_8_Content_measurement", consent?._purposes[8] ? 'true' : 'false')
					googletag.pubads().setTargeting("IAB_9_Market_research", consent?._purposes[9] ? 'true' : 'false')
					googletag.pubads().setTargeting("IAB_10_Product_development", consent?._purposes[10] ? 'true' : 'false')
					googletag.pubads().setTargeting("IAB_SF1_Precise_geo", consent?._specialFeatureOptins[1] ? 'true' : 'false')
				}

				if (!consent || typeof consent._purposes == 'undefined' || consent?._purposes[1] != true) {
					// googletag.pubads().setCookieOptions(1)
					googletag.pubads().setPrivacySettings({ nonPersonalizedAds: true })
				}


				googletag.pubads().setCentering(true)
				googletag.pubads().disableInitialLoad()
				//googletag.pubads().enableSingleRequest()

				log('-- Load onBeforeStart')
				publisherConfig.onBeforeStart?.call(this, { consent: consent, loadJs, loadPixel })

				if (consent && consent._consentString) {
					if (publisherConfig.config.enableSmartClipPixel ?? true) {
						loadJs('https://des.smartclip.net/ads?type=dyn&plc=106864&sz=1x1&consent=' + (consent._consentString ?? '') + '&rnd=' + (Math.floor(9e8 * Math.random()) + 1e8));
					}
				}

				// Set targeting URL
				googletag.pubads().setTargeting("url", targetingUrl);

				if (publisherConfig.debug && (publisherConfig.config.enableTestTargeting ?? true)) {
					googletag.pubads().setTargeting("test", ["true"]);
				}

				if (publisherConfig.config.enablePageTypeTargeting ?? true) {
					// const homepages = publisherConfig.config.homepages ?? [];
					// const customPageType = publisherConfig.config.customPageType ?? null;
					// let pageType = isHome(homepages) ? 'homepage' : 'artikel';
					// if (customPageType) {
					// 	pageType = customPageType;
					// }
					let pageType:string = getPageType()
					googletag.pubads().setTargeting("pagetype", pageType);
				}

				if (publisherConfig.config.extraTargeting) {
					for (const key in publisherConfig.config.extraTargeting) {
						if (Object.prototype.hasOwnProperty.call(publisherConfig.config.extraTargeting, key)) {
							const value = publisherConfig.config.extraTargeting[key];

							googletag.pubads().setTargeting(key, value);
						}
					}
				}

				googletag.enableServices()

				log('-- GPT Started')
			})

			return Promise.resolve(true)

		})
	}

})()